import type { Exercise } from './exercise'

export type TrainingPlanStatus = 'draft' | 'pending' | 'active' | 'rejected' | 'completed'
export type TrainingPlanDiscipline = 'sport_trad' | 'bouldering'
export type TrainingExperience = 'beginner' | 'intermediate' | 'advanced'
export type TrainingPlanWeekKind = 'base' | 'rest'

export type WeekDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday'

export const WEEK_DAYS: WeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

export interface PlanUserSummary {
  id: string
  username: string
  name: string | null
  avatar_small_url?: string | null
}

export interface TrainingPlanWeek {
  id: string
  week_number: number
  kind: TrainingPlanWeekKind
  description: string | null
}

export interface TrainingPlanExercise {
  id: string
  exercise_id: number
  position: number
  exercise: Exercise
}

export interface TrainingPlanCell {
  week_number: number
  exercise_id: number
  frequency: number
}

export interface TrainingPlan {
  id: string
  status: TrainingPlanStatus
  start_date: string | null
  discipline: TrainingPlanDiscipline | null
  training_experience: TrainingExperience | null
  length_weeks: number | null
  session_duration_minutes: number | null
  sessions_per_week: number | null
  preferred_session_days: WeekDay[]
  goal: string | null
  typical_grade: string | null
  best_grade: string | null
  style_strengths: string | null
  style_limiters: string | null
  recent_training: string | null
  facilities: string | null
  work_demands: string | null
  other_activity: string | null
  injuries_constraints: string | null
  preferences: string | null
  user: PlanUserSummary
  coach: PlanUserSummary | null
  weeks: TrainingPlanWeek[]
  exercises: TrainingPlanExercise[]
  cells: TrainingPlanCell[]
  created_at: string | null
}

export interface WeekPayload {
  week_number: number
  kind: TrainingPlanWeekKind
  description?: string | null
}

export interface ExercisePayload {
  exercise_id: number
  position: number
}

export interface CellPayload {
  week_number: number
  exercise_id: number
  frequency: number
}

export interface TrainingPlanPayload {
  start_date?: string | null
  discipline?: TrainingPlanDiscipline | null
  training_experience?: TrainingExperience | null
  length_weeks?: number | null
  session_duration_minutes?: number | null
  sessions_per_week?: number | null
  preferred_session_days?: WeekDay[]
  goal?: string | null
  typical_grade?: string | null
  best_grade?: string | null
  style_strengths?: string | null
  style_limiters?: string | null
  recent_training?: string | null
  facilities?: string | null
  work_demands?: string | null
  other_activity?: string | null
  injuries_constraints?: string | null
  preferences?: string | null
  weeks?: WeekPayload[]
  exercises?: ExercisePayload[]
  cells?: CellPayload[]
  /** The athlete (always the requester) invites this coach to build the plan. */
  coach_id?: string
  /** Secret token from a coach's invite link; resolves the coach server-side. */
  coach_token?: string
  /** Week numbers to regenerate on the diary (only applied while the plan is active). */
  apply_week_numbers?: number[]
}
